import { Component, OnInit, Input } from '@angular/core';
import { Unit, LocationUnits, get_space_estimate } from 'services/unit.service';
import { Location } from 'services/location.service';
import { CueWidgetService } from 'services/cue-widget.service';

interface UnitGroup {
	name: string;
	subtitle: string;
	icon: string;
	sizerank: string;
	units: Unit[];
	minPrice: number;
	expanded: boolean;
	available: boolean;
}

@Component({
	selector: 'ras-unit-grid-grouped-b',
	templateUrl: './unit-grid-grouped-b.component.html',
	styleUrls: ['./unit-grid-grouped-b.component.sass']
})
export class UnitGridGroupedBComponent implements OnInit {

	@Input() location: Location;
	@Input('units') set _units(value: LocationUnits) {
		this.units = value;
		this.show_unit_img = new Set();
		if (value) {
			this.groupUnits();
		}
	}

	public units: LocationUnits = null;
	public unitGroups: UnitGroup[] = [];
	public show_unit_img = new Set();
	public starting_rate: number = null;

	constructor(private cueWidget: CueWidgetService) { }

	get_estimate(unit: Unit): string {
		return get_space_estimate(unit)
	}

	openWhatsApp() {
		this.cueWidget.openWidget();
	}

	getSizeName(fullName: string): string {
		// Returns the size part (e.g., "Small", "Medium", "Large", "Parking")
		return fullName.split(' ')[0];
	}

	getUnitsWord(fullName: string): string {
		// Returns the "units" or "bays" part
		const words = fullName.split(' ');
		return words.slice(1).join(' ');
	}

	groupUnits() {
		// Calculate starting rate across all units
		this.starting_rate = null;
		for (let size in this.units.by_size) {
			let units = this.units.by_size[size];
			for (let unit of units) {
				let rate = Number.parseFloat(unit.rate.toString());
				if (this.starting_rate == null || this.starting_rate > rate) {
					this.starting_rate = unit.rate;
				}
			}
		}

		const groups: UnitGroup[] = [
			{
				name: 'Small units',
				subtitle: 'Compact and affordable.',
				icon: 'S',
				sizerank: 'small',
				units: [],
				minPrice: null,
				expanded: false,
				available: true
			},
			{
				name: 'Medium units',
				subtitle: 'Ideal for life’s transitions.',
				icon: 'M',
				sizerank: 'medium',
				units: [],
				minPrice: null,
				expanded: false,
				available: true
			},
			{
				name: 'Large units',
				subtitle: 'Space for the big moves.',
				icon: 'L',
				sizerank: 'large',
				units: [],
				minPrice: null,
				expanded: false,
				available: true
			},
			{
				name: 'Parking bays',
				subtitle: 'Secure spots for vehicles.',
				icon: 'P',
				sizerank: 'parking',
				units: [],
				minPrice: null,
				expanded: false,
				available: true
			}
		];

		// Populate groups with units and find minimum prices
		for (const group of groups) {
			if (this.units.by_size[group.sizerank]) {
				const allUnits = this.units.by_size[group.sizerank];

				// Group by exact dimension, then keep only cheapest and most expensive per dim
				const byDim = new Map<string, Unit[]>();
				for (const unit of allUnits) {
					if (!byDim.has(unit.dim)) byDim.set(unit.dim, []);
					byDim.get(unit.dim).push(unit);
				}

				// For each dim: sort by price, keep min+max, record sqm for ordering
				const dimGroups: { area: number; units: Unit[] }[] = [];
				for (const [dim, dimUnits] of byDim.entries()) {
					const sorted = dimUnits.sort((a, b) => a.rate - b.rate);
					const kept: Unit[] = [sorted[0]];
					if (sorted.length > 1) kept.push(sorted[sorted.length - 1]);
					const parts = dim.split('x').map(s => parseFloat(s.trim()));
					const area = parts.reduce((a, b) => a * b, 1);
					dimGroups.push({ area, units: kept });
				}

				// Sort dim groups by physical size (sqm) ascending, then flatten
				dimGroups.sort((a, b) => a.area - b.area);
				group.units = ([] as Unit[]).concat(...dimGroups.map(g => g.units));
				group.available = group.units.length > 0;

				// Calculate minimum price
				if (group.units.length > 0) {
					group.minPrice = group.units[0].rate;

					// Show parking bay images by default
					if (group.sizerank === 'parking') {
						group.units.forEach(unit => this.show_unit_img.add(unit.id));
					}
				}
			} else {
				group.available = false;
			}
		}

		// Determine which group to expand by default
		// Priority: 1. Medium (most popular), 2. Small, 3. Large
		const mediumGroup = groups.find(g => g.sizerank === 'medium');
		const smallGroup = groups.find(g => g.sizerank === 'small');
		const largeGroup = groups.find(g => g.sizerank === 'large');

		if (mediumGroup && mediumGroup.available) {
			mediumGroup.expanded = true;
		} else if (smallGroup && smallGroup.available) {
			smallGroup.expanded = true;
		} else if (largeGroup && largeGroup.available) {
			largeGroup.expanded = true;
		}

		this.unitGroups = groups;
	}

	toggleGroup(group: UnitGroup, headerElement?: HTMLElement) {
		// Close all other groups first
		this.unitGroups.forEach(g => {
			if (g !== group) {
				g.expanded = false;
			}
		});
		// Toggle the clicked group
		group.expanded = !group.expanded;

		// Scroll to the accordion header immediately using requestAnimationFrame
		if (headerElement && group.expanded) {
			requestAnimationFrame(() => {
				const offset = 80; // Offset for fixed header if any
				const elementPosition = headerElement.getBoundingClientRect().top;
				const offsetPosition = elementPosition + window.pageYOffset - offset;

				window.scrollTo({
					top: offsetPosition,
					behavior: 'auto' // Instant scroll - no jumping
				});
			});
		}
	}

	quick_quote(unit: Unit, location: Location) {
		if ((<any>window).dataLayer) {
			(<any>window).dataLayer.push({
				'event': 'add-to-cart',
				'facility_name': location.name,
				'unit_size': unit.dim
			});
		}
	}

	is_open_parking(unit: Unit): boolean {
		return (
			unit.desc.search("Open Parking") >= 0 ||
			unit.desc.search("Open Air Parking") >= 0
		);
	}

	ngOnInit(): void {
	}

}
